"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotographIcon,
  SwitchHorizontalIcon,
  ArrowLeftIcon,
  SearchIcon,
} from "@heroicons/react/outline"

const Service = () => {
  const [services, setServices] = useState([])
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    description: "",
    image: null,
    status: true,
  })
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [preview, setPreview] = useState(null)
  const [activeTab, setActiveTab] = useState("list")
  const [searchTerm, setSearchTerm] = useState("")

  const API_URL = "https://server.ananikaet.com/api/service"

  // Fetch all services
  const fetchServices = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios.get(API_URL)
      // Check if data.services exists, otherwise use data directly
      const servicesList = data.services || data
      setServices(servicesList)
    } catch (err) {
      setError("Failed to fetch services. Please try again later.")
      console.error("Error fetching services:", err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
  }, [])

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }))

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      id: null,
      title: "",
      description: "",
      image: null,
      status: true,
    })
    setIsEditing(false)
    setPreview(null)
    setActiveTab("list")
  }

  // Submit form (create or update service)
  const handleSubmit = async (e) => {
    e.preventDefault()
    const formPayload = new FormData()
    formPayload.append("title", formData.title)
    formPayload.append("description", formData.description)
    formPayload.append("status", formData.status)
    if (formData.image) {
      formPayload.append("image", formData.image)
    }

    try {
      setError(null)
      setLoading(true)
      if (isEditing) {
        // Update service
        await axios.put(`${API_URL}/${formData.id}`, formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        setSuccess("Service updated successfully!")
      } else {
        // Create new service
        await axios.post(`${API_URL}/create`, formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        setSuccess("Service created successfully!")
      }
      fetchServices()
      resetForm()

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 3000)
    } catch (err) {
      setError("Failed to save the service. Please try again.")
      console.error("Error saving service:", err.message)
    } finally {
      setLoading(false)
    }
  }

  // Handle service edit
  const handleEdit = (service) => {
    setFormData({
      id: service._id,
      title: service.title,
      description: service.description,
      status: service.status || true,
      image: null, // Only set a new image when uploading
    })
    setIsEditing(true)
    setActiveTab("form")
  }

  // Handle service delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this service?")) {
      try {
        setError(null)
        await axios.delete(`${API_URL}/${id}`)
        setSuccess("Service deleted successfully!")
        fetchServices()

        // Auto-clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(null)
        }, 3000)
      } catch (err) {
        setError("Failed to delete the service. Please try again.")
        console.error("Error deleting service:", err.message)
      }
    }
  }

  // Filter services based on search term
  const filteredServices = services.filter(
    (service) =>
      service.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
<div className="flex flex-col min-h-screen px-4 pt-6 pb-6 lg:pl-64 bg-gray-50">
  {/* Sticky Heading - Added more top padding for mobile */}
  <div className="sticky top-0 z-10 bg-gray-50 pt-16 md:pt-2 pb-4 mb-6">
    <div className="flex justify-between items-center">
      <div>
        <h2 className="text-3xl md:text-1xl font-bold text-blue-900">Service Management</h2>
        <div className="h-1 w-20 bg-blue-600 mt-2 rounded-full"></div>
      </div>

      {activeTab === "list" ? (
        <button
          onClick={() => setActiveTab("form")}
          className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Service
        </button>
      ) : (
        <button
          onClick={() => {
            resetForm()
            setActiveTab("list")
          }}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to List
        </button>
      )}
    </div>
  </div>


      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {success}
        </div>
      )}

      {/* Service Form */}
      {activeTab === "form" && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h3 className="text-xl font-medium text-gray-800 mb-6">
            {isEditing ? "Edit Service" : "Create New Service"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Service Title"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Service Description"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[150px]"
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="status"
                    name="status"
                    type="checkbox"
                    checked={formData.status}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="status"
                    className="flex items-center text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    <SwitchHorizontalIcon className="w-4 h-4 mr-2 text-gray-500" />
                    {formData.status ? "Active" : "Inactive"}
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Image</label>
                  <div className="border-2 border-dashed rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer">
                    <label className="flex flex-col items-center justify-center cursor-pointer">
                      {preview ? (
                        <div className="relative w-full aspect-video mb-4">
                          <img
                            src={preview || "/placeholder.svg"}
                            alt="Preview"
                            className="rounded-md object-cover w-full h-full"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-6">
                          <PhotographIcon className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 text-center">
                            Click to upload an image
                            <br />
                            <span className="text-xs">PNG, JPG or GIF</span>
                          </p>
                        </div>
                      )}
                      <input type="file" name="image" onChange={handleFileChange} className="hidden" accept="image/*" />
                    </label>
                  </div>
                  {isEditing && !preview && (
                    <p className="text-xs text-yellow-500 mt-2 flex items-center">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      Leave empty to keep the current image
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white min-w-[120px] ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isEditing ? "Updating..." : "Creating..."}
                  </>
                ) : isEditing ? (
                  "Update Service"
                ) : (
                  "Create Service"
                )}
              </button>

              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === "list" && (
        <>
          {/* Search Bar */}
          <div className="mb-6 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search services by title or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Services List */}
          {loading && services.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <PhotographIcon className="w-16 h-16 mb-3 opacity-20" />
              <p className="text-lg font-medium">No services available</p>
              <p className="text-sm mb-4">
                {searchTerm ? "Try adjusting your search" : "Create your first service to get started"}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setActiveTab("form")}
                  className="inline-flex items-center px-2 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Service
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => (
                <div
                  key={service._id}
                  className="bg-white rounded-lg overflow-hidden shadow-lg transition-all hover:shadow-xl"
                >
                  <div className="relative aspect-video">
                    <img
                      src={
                        service.image
                          ? `https://server.ananikaet.com/${service.image}`
                          : service.imageUrl
                            ? `https://server.ananikaet.com//${service.imageUrl}`
                            : "https://via.placeholder.com/400x200"
                      }
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      {service.status ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2 text-gray-800">{service.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3">{service.description}</p>
                  </div>
                  <div className="p-4 pt-0 flex justify-between">
                    <button
                      onClick={() => handleEdit(service)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(service._id)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Service

